//@ts-ignore
import type * as PDFJS from "pdfjs-dist";
import FileReader from "@/commons/utils/FileReaderWrapper";
//@ts-ignore
import * as pdfjs from "pdfjs-dist/legacy/build/pdf";
import SystemError from "../models/SystemError";

/**
 * ### Fileオブジェクト(PDF)をjpegのDataUrlに変換する
 */
export default class PdfUtil {
  /**
   * ### PDFJSのPDFドキュメント情報を取得する
   */
  public static async getPdfDocument(file: File): Promise<PdfUtilResult> {
    // 入力チェック
    this.validationFile(file);

    const result: PdfUtilResult = {
      name: file.name,
      data: undefined,
      dataUrl: undefined,
      status: PdfUtilResultStatus.NO_ERROR
    };

    try {
      const dataUrl: string = await this.convertDataUrl(file);
      result.data = await this.convertPdf(dataUrl);
    } catch (error) {
      console.error(error);
      // パスワード付きPDFだった場合はエラーコードをパスワード付きにする
      const systemError: SystemError = error as unknown as SystemError;
      const message: string = systemError.message as string;
      if (message.match(/PasswordException/)) {
        result.data = undefined;
        (result.dataUrl = undefined), (result.status = PdfUtilResultStatus.LOCKED);
      } else {
        result.data = undefined;
        (result.dataUrl = undefined), (result.status = PdfUtilResultStatus.UNKNOWN);
      }
    }

    return result;
  }

  /**
   * ### PDFJSのPDFドキュメントをJpegに変換する
   *
   * @param pdf
   * @param fileName
   * @param pageNum
   */
  public static async convertPdfDocumentToJpeg(pdf: PDFJS.PDFDocumentProxy, fileName: string, pageNum: number): Promise<PdfUtilResult> {
    // 入力チェック
    this.validationPageNum(pageNum);

    const result: PdfUtilResult = {
      name: `${fileName}_${pageNum}page`,
      data: undefined,
      dataUrl: undefined,
      status: PdfUtilResultStatus.NO_ERROR
    };

    try {
      // 読込まれたPDFのページ数と読込み対象ページ番号が不一致の場合はエラーとする
      if (pdf.numPages < pageNum) {
        result.data = undefined;
        result.dataUrl = undefined;
        result.status = PdfUtilResultStatus.INVALID_PAGENUM;
        return result;
      }

      const page: PDFJS.PDFPageProxy = await this.pdfjsGetPage(pdf, pageNum);
      const canvas: HTMLCanvasElement = await this.convertCanvas(page);
      result.data = pdf;
      result.dataUrl = canvas.toDataURL("image/jpeg");
    } catch (error) {
      // パスワード付きPDFだった場合はエラーコードをパスワード付きにする
      const systemError: SystemError = error as unknown as SystemError;
      const message: string = systemError.message as string;
      if (message.match(/PasswordException/)) {
        result.data = undefined;
        result.dataUrl = undefined;
        result.status = PdfUtilResultStatus.LOCKED;
      } else {
        result.data = undefined;
        result.dataUrl = undefined;
        result.status = PdfUtilResultStatus.UNKNOWN;
      }
    }

    return result;
  }

  /**
   * ### PDFファイルをJpegに変換する
   *
   * - ページ番号直接指定で変換する
   *
   * @param file Fileオブジェクト
   * @param pageNum 読込み対象ページ番号(1始まり)
   * @returns jpegのDataUrl
   */
  public static async convertFileToJpeg(file: File, pageNum: number): Promise<PdfUtilResult> {
    // 入力チェック
    this.validationFile(file);
    this.validationPageNum(pageNum);

    let result: PdfUtilResult = {
      name: `${file.name}_${pageNum}page`,
      data: undefined,
      dataUrl: undefined,
      status: PdfUtilResultStatus.NO_ERROR
    };

    try {
      const dataUrl: string = await this.convertDataUrl(file);
      const pdf: PDFJS.PDFDocumentProxy = await this.convertPdf(dataUrl);

      result = await this.convertPdfDocumentToJpeg(pdf, file.name, pageNum);

      // 使用したPDFJSドキュメントを削除する
      pdf.destroy();
    } catch (error) {
      // パスワード付きPDFだった場合はエラーコードをパスワード付きにする
      const systemError: SystemError = error as unknown as SystemError;
      const message: string = systemError.message as string;
      if (message.match(/PasswordException/)) {
        result.data = undefined;
        result.dataUrl = undefined;
        result.status = PdfUtilResultStatus.LOCKED;
      } else {
        result.data = undefined;
        result.dataUrl = undefined;
        result.status = PdfUtilResultStatus.UNKNOWN;
      }
    }

    return result;
  }

  /**
   * ### 入力値チェック
   *
   * - ファイル種別がPDFで無ければエラー
   *
   * @param file
   */
  public static validationFile(file: File): void {
    if (file.type != "application/pdf") {
      const message: string = "file type is not 'application/pdf'";
      const systemError: SystemError = new SystemError(message);
      systemError.errorMessage = message;
      systemError.message = message;
      systemError.request = `${this.constructor.name} convertDataUrl()`;

      throw systemError;
    }

    return;
  }

  /**
   * ### 入力値チェック
   *
   * - ページ番号が1未満でエラー
   *
   * @param pageNum
   */
  public static validationPageNum(pageNum: number): void {
    if (pageNum < 1) {
      const message: string = "pageNum is invalid, the pageNum must be 1 or higher";
      const systemError: SystemError = new SystemError(message);
      systemError.errorMessage = message;
      systemError.message = message;
      systemError.request = `${this.constructor.name} convertDataUrl()`;

      throw systemError;
    }

    return;
  }

  /**
   * ### FileオブジェクトをDataURLに変換する
   *
   * @param file Fileオブジェクト
   */
  public static async convertDataUrl(file: File): Promise<string> {
    const dataUrl: string = await FileReader.readAsDataURL(file);
    return dataUrl;
  }

  /**
   * ### DataURLをPDFJSに読込ませてドキュメント情報に変換する
   *
   * @param dataUrl DataURL
   */
  public static async convertPdf(dataUrl: string): Promise<PDFJS.PDFDocumentProxy> {
    const pdf: PDFJS.PDFDocumentProxy = await this.pdfjsGetDocument(dataUrl);
    return pdf;
  }

  /**
   * ### DataURLからPDFJSのドキュメントを読込む
   *
   * - PDFJSはPromiseを返さないので、Promiseを返すようにwrapする
   * - PDFJSは軽量化のためにデフォルトではフォント(cmaps)は読込まないので、自前で読込ませる
   * - cMapUrlはURLを指定する必要があるので、cmapsファイルはpublicディレクトリに格納して、パス指定している
   *
   * @param dataUrl
   */
  public static async pdfjsGetDocument(dataUrl: string): Promise<PDFJS.PDFDocumentProxy> {
    return new Promise((resolve: any, reject: any) => {
      // webpack5.xになったので、public/pdfjsにworkerを格納するのではなく、worker()で作成する
      pdfjs.GlobalWorkerOptions.workerPort = new Worker(new URL("pdfjs-dist/legacy/build/pdf.worker.mjs", import.meta.url));
      const pdfLoading: Promise<PDFJS.PDFDocumentProxy> = pdfjs.getDocument({
        // フォントの読込みが必要な場合があるので、cMapUrlを指定する
        // public/pdfjs/cmapsにフォントファイルは格納されている
        // cMap(フォント)はバイナリデータなのでcMapPackedはtrueを指定する
        url: dataUrl,
        cMapUrl: "/pdfjs/cmaps/",
        cMapPacked: true
      }).promise;
      pdfLoading.then(
        (pdfDoc: PDFJS.PDFDocumentProxy) => {
          resolve(pdfDoc);
        },
        (reason: string) => {
          reject(new Error(reason));
        }
      );
    });
  }

  /**
   *  ### PDFJSドキュメントからページ情報を読み込む
   *
   * - PDFJSはPromiseを返さないので、Promiseを返すようにwrapしている
   *
   * @param pdf PDFドキュメント
   * @param pageNum 読込むページ番号
   *
   * @returns PDFページ情報
   */
  public static async pdfjsGetPage(pdf: PDFJS.PDFDocumentProxy, pageNum: number): Promise<PDFJS.PDFPageProxy> {
    return new Promise((resolve: any, reject: any) => {
      pdf.getPage(pageNum).then(
        (page: PDFJS.PDFPageProxy) => {
          resolve(page);
        },
        (reason: string) => {
          reject(new Error(reason));
        }
      );
    });
  }

  /**
   * ### PDFページ情報からcanvas要素に変換する
   *
   * @param page PDFJSページ情報
   *
   * @returns HTMLCanvasElement
   */
  public static async convertCanvas(page: PDFJS.PDFPageProxy): Promise<HTMLCanvasElement> {
    // canvasの大きさを決めるために、viewportが必要
    // PDFのページをviewportとして読込む
    const viewport: PDFJS.PageViewport = page.getViewport({ scale: 1 });

    // PDFを表示するためのcanvasを作る
    const canvas: HTMLCanvasElement = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    // canvasからcontextを作る
    const context: CanvasRenderingContext2D | null = canvas.getContext("2d");

    // PDFの内容をcanvasに描画する
    if (context) {
      const renderParams: any = {
        canvasContext: context,
        viewport: viewport
      };
      await this.pdfjsPageRender(page, renderParams);
    }

    return canvas;
  }

  /**
   * ### PDFJSのページレンダリング
   *
   * - PDFJSはPromiseを返さないのでwrapしている
   * - 引数で渡されたレンダリング設定に含まれるcanvas要素にページ内容をレンダリングする
   *
   * @param page PDFJSページ情報
   * @param renderParams レンダリング設定
   */
  public static async pdfjsPageRender(page: PDFJS.PDFPageProxy, renderParams: any) {
    return new Promise((resolve: any, reject: any) => {
      const pageLoading = page.render(renderParams).promise;
      pageLoading.then(
        () => {
          resolve();
        },
        (reason: string) => {
          reject(new Error(reason));
        }
      );
    });
  }
}

/**
 * ### PDFからJpegへの変換結果
 *
 * - PDFが正常だが変換できないパターンがあるのでオブジェクトを定義してい返却する
 */
export interface PdfUtilResult {
  /** ファイル名 */
  name: string;
  /** 読込みまたは変換したデータ */
  data?: PDFJS.PDFDocumentProxy;
  /** jpeg変換した場合はDataURL */
  dataUrl?: string;
  /** PDFからJpegへの変換エラーコード */
  status: PdfUtilResultStatus;
}

/**
 * ### PDFからJpegへの変換エラーコード
 */
export enum PdfUtilResultStatus {
  /** エラーなし */
  NO_ERROR = 0,
  /** パスワード付きPDF */
  LOCKED = 1,
  /** ページ番号不正 */
  INVALID_PAGENUM = 2,
  /** 変換不可 */
  UNKNOWN = 3
}
